import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { grafanaQueryApiRef } from '../api/GrafanaQueryApi';
import { useRefreshInterval } from './useRefreshInterval';
import type { PrometheusQuery, DataFrameResult, TimeRange } from '../api/types';

interface UseGrafanaQueryResult {
  data: DataFrameResult | undefined;
  loading: boolean;
  error: Error | undefined;
  refresh: () => void;
}

/**
 * Hook that executes PromQL queries via the Grafana proxy and auto-refreshes.
 */
export function useGrafanaQuery(
  queries: PrometheusQuery[],
  timeRange: TimeRange,
  options?: { refreshInterval?: number },
): UseGrafanaQueryResult {
  const api = useApi(grafanaQueryApiRef);
  const [data, setData] = useState<DataFrameResult | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const { interval: userRefreshInterval } = useRefreshInterval();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!queries.length) {
      setData(undefined);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const result = await api.queryPrometheus(queries, timeRange);
      if (mountedRef.current) {
        setData(result);
        setError(undefined);
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [api, queries, timeRange]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    const interval = options?.refreshInterval ?? userRefreshInterval;
    if (interval > 0) {
      intervalRef.current = setInterval(fetchData, interval);
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData, options?.refreshInterval, userRefreshInterval]);

  return { data, loading, error, refresh: fetchData };
}
